import { Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import moment from 'moment';
import { SmDataService } from './sm-data.service';
import { UtilityService } from '@core/services/utility.service';

export interface InitLoadResult {
  success: boolean;
  error?: string;
  needsReload?: boolean;
  reloadMessage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SmOfflineService {
  private readonly smDataService = inject(SmDataService);
  private readonly utilityService = inject(UtilityService);
  public indexedDBVersion = 31;
  public allDBTable = [
    'SM_GetWorkOrderTripsIsHistory',
    'SM_GetWorkOrderTripsNotHistory',
    'SM_GetServiceItemWOHistory',
    'SM_GetWorkOrderScopesIsHistory',
    'SM_GetWorkOrderScopesNotHistory',
    'SM_GetWorkCompleted',
    'SM_GetTechnicians',
    'SM_GetServiceItemDetails',
    'SM_GetCommonLookups',
    'SM_GetEquipRevCode',
    'SM_GetSettingsBySMCo',
    'SM_GetScopeTasks',
    'SM_GetScopePurchaseOrders',
    'SM_GetFormsPerScope',
    'SM_GetFormTemplateOptions',
    'SM_GetSignerContacts',
    'SM_GetCustomers',
    'SM_GetServiceCenters',
    'SM_GetSitesByCustomer',
    'SM_GetServiceItemParts',
    'SM_GetMaterial',
    'SM_GetStandardItems',
    'SM_GetEquipment',
    'SM_GetMaterialWLoc',
    'SM_GetSettingsTable',
    'SM_OfflineSyncTrip',
    'SM_WCCustomFieldDefs',
    'SM_GetCommonWoLookups',
  ];

  initializeDB(enableOfflineFS: boolean): Observable<InitLoadResult> {
    return new Observable<InitLoadResult>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);
      indexdb.onerror = (): void => {
        subscriber.next({
          success: false,
          needsReload: true,
          reloadMessage: 'Database error. Page will reload in few secs...',
        });
        subscriber.complete();
      };

      indexdb.onblocked = (): void => {
        subscriber.next({
          success: false,
          needsReload: true,
          reloadMessage: 'Database blocked. Page will reload in few secs...',
        });
        subscriber.complete();
      };

      indexdb.onupgradeneeded = (): void => {
        window.indexedDB.deleteDatabase('KDS_SM_DB');
        subscriber.next({
          success: false,
          needsReload: true,
          reloadMessage: 'Database needs to upgrade. Page will reload in few secs...',
        });
        subscriber.complete();
      };

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;

        if (db.objectStoreNames.length !== 30) {
          window.indexedDB.deleteDatabase('KDS_SM_DB');
          if (window.navigator.serviceWorker.controller != null) {
            window.navigator.serviceWorker.controller.postMessage({
              shouldOfflineSync: false,
              installDatabase: true,
            });
          }
          subscriber.next({
            success: false,
            needsReload: true,
            reloadMessage: 'Database initiating...Page will reload when it is ready.',
          });
          subscriber.complete();
          return;
        }

        if (window.navigator.serviceWorker.controller != null && enableOfflineFS) {
          window.navigator.serviceWorker.controller.postMessage({
            shouldOfflineSync: true,
            installDatabase: false,
            storeOfflineSyncTrip: false,
          });
        }

        this.smDataService.initLoading = false;

        if (this.smDataService.currentOnline) {
          this.loadOfflinePostCalls().subscribe(success => {
            if (success) {
              this.loadOfflineSyncFailures().subscribe(() => {
                subscriber.next({ success: true });
                subscriber.complete();
              });
            }
          });
        } else {
          subscriber.next({ success: true });
          subscriber.complete();
        }
      };
    });
  }

  getRecords<T>(tableName: string): Observable<T[]> {
    return new Observable<T[]>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction(tableName, 'readonly');
        const storage = transaction.objectStore(tableName);
        const request = storage.getAll();

        request.onsuccess = (): void => {
          subscriber.next((request.result as T[]) || []);
          subscriber.complete();
        };

        request.onerror = (): void => {
          subscriber.error();
          subscriber.complete();
        };
      };

      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  deleteRecords(tableName: string, iDsToDelete: number[]): Observable<void> {
    return new Observable<void>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);
      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction(tableName, 'readwrite');
        const storage = transaction.objectStore(tableName);
        iDsToDelete.forEach(id => {
          storage.delete(id);
        });
        subscriber.next();
        subscriber.complete();
      };
    });
  }

  addorUpdateRecord<T>(tableName: string, record: T): Observable<void> {
    return new Observable<void>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction(tableName, 'readwrite');
        const storage = transaction.objectStore(tableName);
        storage.put(record);
        subscriber.next();
        subscriber.complete();
      };
      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  deleteOldRecordsFromAllTables(): void {
    const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

    indexdb.onsuccess = (): void => {
      const db = indexdb.result;
      this.allDBTable.forEach(val => {
        const transaction = db.transaction(val, 'readwrite');
        const storage = transaction.objectStore(val);
        const request = storage.getAll();

        request.onsuccess = (): void => {
          const today = moment().format('YYYY-MM-DD');
          request.result.forEach(record => {
            if (moment(record.InsertDate).format('YYYY-MM-DD') !== today) {
              storage.delete(record[storage.keyPath.toString()]);
            }
          });
        };
      });
    };
  }

  updateTripStatus(tripId: number, status: string): void {
    this.smDataService.tripList.forEach(trip => {
      if (trip.SMTripID === tripId) trip.TripStatus = status;
    });

    this.smDataService.tripListFiltered.forEach(trip => {
      if (trip.SMTripID === tripId) trip.TripStatus = status;
    });

    const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);
    indexdb.onsuccess = (): void => {
      const db = indexdb.result;
      const transaction = db.transaction('SM_GetWorkOrderTripsNotHistory', 'readwrite');
      const storage = transaction.objectStore('SM_GetWorkOrderTripsNotHistory');
      const keyRangeValue = IDBKeyRange.only(tripId);

      const cursorRequest = storage.openCursor(keyRangeValue);
      cursorRequest.onsuccess = (event: any): void => {
        const cursor = event.target.result;
        const data = cursor.value;
        data.TripStatus = status;
        cursor.update(data);
      };
    };
  }

  clearObjectStore(tableName: string): Observable<void> {
    return new Observable<void>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction(tableName, 'readwrite');
        const storage = transaction.objectStore(tableName);
        storage.clear();
        subscriber.next();
        subscriber.complete();
      };
      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  updateTripNotes(tripID: number, tripNotes: string, workOrderDescription: string, tripDescription: string): void {
    const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);
    indexdb.onsuccess = (): void => {
      const db = indexdb.result;
      const transaction = db.transaction('SM_GetWorkOrderTripsNotHistory', 'readwrite');
      const storage = transaction.objectStore('SM_GetWorkOrderTripsNotHistory');
      const keyRangeValue = IDBKeyRange.only(tripID);

      const cursorRequest = storage.openCursor(keyRangeValue);
      cursorRequest.onsuccess = (event: any): void => {
        const cursor = event.target.result;
        const data = cursor.value;
        data.TripNotes = tripNotes;
        data.WorkOrderDescription = workOrderDescription;
        data.TripDescription = tripDescription;
        cursor.update(data);
      };
    };
  }

  getWorkCompletedByScopeID(scopeID: number): Observable<any[]> {
    return new Observable<any[]>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction('SM_GetWorkCompleted', 'readwrite');
        const index = transaction.objectStore('SM_GetWorkCompleted').index('filter_by_WorkOrderScopeID');
        const keyRangeValue = IDBKeyRange.only(scopeID);
        const cursor = index.getAll(keyRangeValue);

        cursor.onsuccess = (event: any): void => {
          subscriber.next(event.target.result);
          subscriber.complete();
        };
        cursor.onerror = (): void => {
          subscriber.error();
          subscriber.complete();
        };
      };
      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  clearDB(): Observable<void> {
    return new Observable<void>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);
      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        this.allDBTable.forEach(tableName => {
          const transaction = db.transaction(tableName, 'readwrite');
          const storage = transaction.objectStore(tableName);
          storage.clear();
        });
        subscriber.next();
        subscriber.complete();
      };
      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  loadOfflinePostCalls(): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      this.getRecords<any>('SM_PostCalls').subscribe(records => {
        setTimeout(() => {
          this.smDataService.offlinePostCallList = records;
          this.smDataService.haveOfflinePost = records.length > 0;

          setTimeout(() => {
            this.smDataService.navbarHeight = this.smDataService.haveOfflinePost
              ? document.getElementById('NavBar')?.offsetHeight - 60 || 0
              : 0;
          }, 1000);
        });
      });

      subscriber.next(true);
      subscriber.complete();
    });
  }

  loadOfflineSyncFailures(): Observable<void> {
    return new Observable<void>(subscriber => {
      this.getRecords<any>('SM_OfflineSyncFail').subscribe(records => {
        setTimeout(() => {
          this.smDataService.offlinePostFailList = records;
          this.smDataService.haveOfflinePostFail = records.length > 0;
        });
      });

      subscriber.next();
      subscriber.complete();
    });
  }

  syncOfflineData(fsSyncCompanyData: any[]): Observable<void> {
    return new Observable<void>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);
      const savedWorkOrderTripIDs = new Set<number>();
      indexdb.onsuccess = (): void => {
        const db = indexdb.result;

        fsSyncCompanyData?.forEach(companyData => {
          this.saveSettingsBySMCo(db, companyData?.Settings, companyData?.SMCo);
          this.saveWCCustomFieldDefs(db, companyData?.WorkCompletedCustomFieldDefs);
          this.saveCommonLookups(db, companyData?.CoCommonLookups, companyData?.SMCo);
          this.saveMaterial(db, companyData?.Material);
          this.saveMaterialWLoc(db, companyData?.Material);

          companyData.WorkOrderData?.forEach(woData => {
            savedWorkOrderTripIDs.add(woData?.WorkOrderTrip?.SMTripID);

            this.saveCommonWoLookups(db, woData?.WOCommonLookups, woData?.WorkOrderTrip?.SMWorkOrderID?.toString());
            this.saveWorkOrderScopesNotHistory(db, woData?.WorkOrderScopes, woData?.WorkOrderTrip?.SMWorkOrderID?.toString());
            this.saveSignerContacts(
              db,
              woData?.SignerContacts,
              woData?.WorkOrderTrip?.SMWorkOrderID?.toString(),
              woData?.WorkOrderTrip?.SMTripID?.toString()
            );
            this.saveServiceItemWOHistory(
              db,
              woData?.ServiceItemWOHistory,
              woData?.WorkOrderTrip?.SMWorkOrderID?.toString(),
              woData?.WorkOrderTrip?.SMServiceSiteID?.toString(),
              woData?.WorkOrderTrip?.SMTripID?.toString()
            );
            this.saveServiceItemDetails(
              db,
              woData?.ServiceItemDetails,
              woData?.WorkOrderTrip?.SMWorkOrderID?.toString(),
              woData?.WorkOrderTrip?.SMServiceSiteID?.toString()
            );

            woData?.WorkOrderScopeData?.forEach(scopeData => {
              this.saveFormTemplateOptions(db, scopeData?.FormTemplateOptions);
              this.saveEquipment(
                db,
                scopeData?.Equipment,
                companyData?.SMCo?.toString(),
                woData?.WorkOrderTrip?.SMTripID?.toString(),
                scopeData?.SMWorkOrderScopeID?.toString()
              );
              this.saveStandardItems(
                db,
                scopeData?.StandardItems,
                companyData?.SMCo?.toString(),
                woData?.WorkOrderTrip.SMTripID?.toString(),
                scopeData?.SMWorkOrderScopeID?.toString()
              );
              this.saveScopeMaterial(
                db,
                scopeData?.Materials,
                companyData?.SMCo?.toString(),
                woData?.WorkOrderTrip.SMTripID?.toString(),
                scopeData?.SMWorkOrderScopeID?.toString()
              );
              this.saveScopeMaterialWLoc(
                db,
                scopeData?.Materials,
                companyData?.SMCo?.toString(),
                woData?.WorkOrderTrip?.SMTripID?.toString(),
                scopeData?.SMWorkOrderScopeID?.toString()
              );
              this.saveScopeTasks(db, scopeData?.ScopeTasks, scopeData?.SMWorkOrderScopeID?.toString());
              this.saveWorkCompleted(db, scopeData?.WorkCompleted, scopeData?.SMWorkOrderScopeID?.toString());
              this.saveFormsPerScope(db, scopeData?.FormsPerScope, scopeData?.SMWorkOrderScopeID?.toString());
              this.saveScopePurchaseOrders(db, scopeData?.PurchaseOrders, scopeData?.SMWorkOrderScopeID?.toString());
            });

            if (window.navigator.serviceWorker.controller != null) {
              window.navigator.serviceWorker.controller.postMessage({
                shouldOfflineSync: true,
                installDatabase: false,
                storeOfflineSyncTrip: true,
                tripID: woData.WorkOrderTrip.SMTripID,
              });
            }
          });
        });

        if (this.smDataService.tripList) {
          this.smDataService.tripList.forEach(trip => {
            if (savedWorkOrderTripIDs.has(trip.SMTripID)) {
              trip.ReadyOffline = true;
            }
          });
        }
      };

      subscriber.next();
      subscriber.complete();
    });
  }

  storeOfflineSignature(hqContactID: any[]): Observable<void> {
    return new Observable<void>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);
      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction('SM_GetWorkOrderScopesNotHistory', 'readwrite');
        const storage = transaction.objectStore('SM_GetWorkOrderScopesNotHistory');
        const keyRangeValue = IDBKeyRange.only(this.smDataService.currentWorkOrderScopeData.SMWorkOrderScopeID);

        const cursor = storage.openCursor(keyRangeValue);
        cursor.onsuccess = (event: any): void => {
          const cursorResult = event.target.result;
          const data = cursorResult.value;

          if (!data.hasOwnProperty('HaveOfflineSign')) data.HaveOfflineSign = [];

          data.HaveOfflineSign.push({
            sMTripID: this.smDataService.currentWorkOrder.SMTripID,
            sMWorkOrderID: this.smDataService.currentWorkOrder.SMWorkOrderID,
            HQContactID: hqContactID[0],
          });
          cursorResult.update(data);

          if (!this.smDataService.currentWorkOrderScopeData.hasOwnProperty('HaveOfflineSign'))
            this.smDataService.currentWorkOrderScopeData.HaveOfflineSign = [];

          this.smDataService.currentWorkOrderScopeData.HaveOfflineSign.push({
            sMTripID: this.smDataService.currentWorkOrder.SMTripID,
            sMWorkOrderID: this.smDataService.currentWorkOrder.SMWorkOrderID,
            HQContactID: hqContactID[0],
          });

          subscriber.next();
          subscriber.complete();
        };

        indexdb.onerror = (): void => {
          subscriber.error();
          subscriber.complete();
        };
      };
    });
  }

  getWorkCompleted(SMWorkOrderScopeID: any): Observable<any[]> {
    return new Observable<any[]>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = () => {
        const db = indexdb.result;
        const transaction = db.transaction('SM_GetWorkCompleted', 'readwrite');
        const workOrderScopeIndex = transaction.objectStore('SM_GetWorkCompleted').index('filter_by_WorkOrderScopeID');
        const keyRangeValue = IDBKeyRange.only(SMWorkOrderScopeID.toString());
        const cursor = workOrderScopeIndex.getAll(keyRangeValue);

        cursor.onsuccess = (event: any) => {
          const results = event.target.result;
          subscriber.next(results);
          subscriber.complete();
        };
      };

      indexdb.onerror = () => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  deleteWorkCompleted(): Observable<void> {
    return new Observable<void>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction('SM_GetWorkCompleted', 'readwrite');
        const storage = transaction.objectStore('SM_GetWorkCompleted');
        const keyRangeValue = IDBKeyRange.only(this.smDataService.editingData.SMWorkCompletedID);
        const cursor = storage.openCursor(keyRangeValue);

        cursor.onsuccess = (event: any): void => {
          const curData = event.target.result;
          curData.delete();
          subscriber.next();
          subscriber.complete();
        };
      };

      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  updateWorkCompleted(type: number): Observable<void> {
    return new Observable<void>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction('SM_GetWorkCompleted', 'readwrite');
        const storage = transaction.objectStore('SM_GetWorkCompleted');
        const keyRangeValue = IDBKeyRange.only(this.smDataService.editingData.SMWorkCompletedID);

        if (this.utilityService.checkIsGuid(this.smDataService.editingData.SMWorkCompletedID)) {
          storage.put(this.smDataService.editingData);
        }

        const cursor = storage.openCursor(keyRangeValue);
        cursor.onsuccess = (event: any): void => {
          const curData = event.target.result;
          const tempData = curData.value;
          tempData.DateCompleted = this.smDataService.editingData.DateCompleted;
          tempData.ServiceItem = this.smDataService.editingData.ServiceItem;
          tempData.SMCostType = this.smDataService.editingData.SMCostType;
          tempData.Technician = this.smDataService.editingData.Technician;
          tempData.PRHoursWorked = this.smDataService.editingData.PRHoursWorked;
          tempData.PRBillableHours =
            type == 2 && !this.smDataService.currentScopeSettings.ShowBillHours
              ? parseFloat(this.smDataService.editingData.PRHoursWorked)
              : parseFloat(this.smDataService.editingData.PRBillableHours);
          tempData.PRPayType = this.smDataService.editingData.PRPayType;
          tempData.PRLaborCode = this.smDataService.editingData.PRLaborCode;
          tempData.EMEMKeyID = this.smDataService.editingData.EMEMKeyID;
          tempData.EQRevCode = this.smDataService.editingData.EQRevCode;
          tempData.EQUnits = this.smDataService.editingData.EQUnits;
          tempData.MiscStandardItem = this.smDataService.editingData.MiscStandardItem;
          tempData.Notes = this.smDataService.editingData.Notes;
          tempData.StartTime = this.smDataService.editingData.StartTime;
          tempData.StopTime = this.smDataService.editingData.StopTime;
          tempData.isClockOut = this.smDataService.editingData.isClockOut;
          tempData.costQty = type == 4 ? this.smDataService.editingData.INQuantity : this.smDataService.editingData.MiscCostQuantity;
          tempData.INLMKeyID = this.smDataService.editingData.INLMKeyID;
          tempData.INMaterial = this.smDataService.editingData.INMaterial;
          tempData.BreakHours = this.smDataService.editingData.BreakHours;
          tempData.CustomFields = this.smDataService.editingData.CustomFields;
          tempData.CostRate = this.smDataService.editingData.CostRate;
          tempData.MiscStandardItemLabel = this.smDataService.editingData.MiscStandardItemLabel;
          curData.update(tempData);

          subscriber.next();
          subscriber.complete();
        };
      };
    });
  }

  offlineScopeInsertUpdate(): Observable<void> {
    return new Observable<void>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);
      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction('SM_GetWorkOrderScopesNotHistory', 'readwrite');
        const storage = transaction.objectStore('SM_GetWorkOrderScopesNotHistory');
        const keyRangeValue = IDBKeyRange.only(this.smDataService.currentWorkOrderScopeData.SMWorkOrderScopeID);

        const cursor = storage.openCursor(keyRangeValue);
        cursor.onsuccess = (event: any): void => {
          const cursorResult = event.target.result;
          const data = cursorResult.value;

          data.WorkScope = this.smDataService.currentWorkOrderScopeData.WorkScope;
          data.PriorityName = this.smDataService.currentWorkOrderScopeData.PriorityName;
          data.ScopeDetails = this.smDataService.currentWorkOrderScopeData.ScopeDetails;
          data.CallType = this.smDataService.currentWorkOrderScopeData.CallType;
          data.ServiceItem = this.smDataService.currentWorkOrderScopeData.ServiceItem?.Code;
          data.ScopeNotes = this.smDataService.currentWorkOrderScopeData.ScopeNotes;
          data.Phase = this.smDataService.currentWorkOrderScopeData.Phase;
          cursorResult.update(data);

          subscriber.next();
          subscriber.complete();
        };
      };

      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  offlineUpdateScopeTasks(smScopeTaskID: any, completed: any): Observable<void> {
    return new Observable<void>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction('SM_GetScopeTasks', 'readwrite');
        const storage = transaction.objectStore('SM_GetScopeTasks');
        const keyRangeValue = IDBKeyRange.only(smScopeTaskID);

        const cursor = storage.openCursor(keyRangeValue);
        cursor.onsuccess = (event: any): void => {
          const cursorResult = event.target.result;
          const data = cursorResult.value;
          data.Completed = completed;
          cursorResult.update(data);

          subscriber.next();
          subscriber.complete();
        };
      };

      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  offlineGetMaterial(): Observable<any> {
    return new Observable<any>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction('SM_GetMaterial', 'readwrite');
        const testIndex = transaction.objectStore('SM_GetMaterial').index('filter_by_SMCo_SMTripID_SMWorkOrderScopeID');
        const keyRangeValue = IDBKeyRange.only([
          this.smDataService.currentWorkOrder.SMCo != null
            ? this.smDataService.currentWorkOrder.SMCo.toString()
            : this.smDataService.currentWorkOrderScopeData.SMCo.toString(),
          this.smDataService.currentWorkOrder.SMTripID.toString(),
          this.smDataService.currentWorkOrderScopeData.SMWorkOrderScopeID.toString(),
        ]);
        const cursor = testIndex.getAll(keyRangeValue);

        cursor.onsuccess = (event: any): void => {
          const results = event.target.result;
          subscriber.next(results);
          subscriber.complete();
        };
      };

      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  deletePostCallByWorkCompletedID(workCompletedID: string): void {
    const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);
    indexdb.onsuccess = (): void => {
      const db = indexdb.result;
      const transaction = db.transaction('SM_PostCalls', 'readwrite');
      const storage = transaction.objectStore('SM_PostCalls');
      const offlinePostCallList = storage.getAll();

      offlinePostCallList.onsuccess = (): void => {
        if (offlinePostCallList.result.length > 0) {
          offlinePostCallList.result.forEach((postCall: any) => {
            if (
              postCall.payload.sMWorkCompletedID === workCompletedID &&
              postCall.id != offlinePostCallList.result[offlinePostCallList.result.length - 1].id
            ) {
              storage.delete(postCall.id);
            }
          });
        }
      };
    };
  }

  offlineGetMaterialWLoc(): Observable<any> {
    return new Observable<any>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction('SM_GetMaterialWLoc', 'readwrite');
        const testIndex = transaction.objectStore('SM_GetMaterialWLoc').index('filter_by_SMCo_SMTripID_SMWorkOrderScopeID');
        const keyRangeValue = IDBKeyRange.only([
          this.smDataService.currentWorkOrder.SMCo != null
            ? this.smDataService.currentWorkOrder.SMCo.toString()
            : this.smDataService.currentWorkOrderScopeData.SMCo.toString(),
          this.smDataService.currentWorkOrder.SMTripID.toString(),
          this.smDataService.currentWorkOrderScopeData.SMWorkOrderScopeID.toString(),
        ]);

        const cursor = testIndex.getAll(keyRangeValue);

        cursor.onsuccess = (event: any): void => {
          const results = event.target.result;

          subscriber.next(results);
          subscriber.complete();
        };
      };

      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  offlineGetEditingData(table: string): Observable<any> {
    return new Observable<any>(subscriber => {
      const indexdb = window.indexedDB.open('KDS_SM_DB', this.indexedDBVersion);

      indexdb.onsuccess = (): void => {
        const db = indexdb.result;
        const transaction = db.transaction(table, 'readwrite');
        const testIndex = transaction.objectStore(table).index('filter_by_SMCo_SMTripID_SMWorkOrderScopeID');
        const keyRangeValue = IDBKeyRange.only([
          this.smDataService.currentWorkOrder.SMCo != null
            ? this.smDataService.currentWorkOrder.SMCo.toString()
            : this.smDataService.currentWorkOrderScopeData.SMCo.toString(),
          this.smDataService.currentWorkOrder.SMTripID.toString(),
          this.smDataService.currentWorkOrderScopeData.SMWorkOrderScopeID.toString(),
        ]);
        const cursor = testIndex.getAll(keyRangeValue);

        cursor.onsuccess = (event: any): void => {
          const results = event.target.result;
          subscriber.next(results);
          subscriber.complete();
        };
      };

      indexdb.onerror = (): void => {
        subscriber.error();
        subscriber.complete();
      };
    });
  }

  private saveSettingsBySMCo(db: IDBDatabase, settings: any, smCo: any): void {
    const transaction = db.transaction('SM_GetSettingsBySMCo', 'readwrite');
    const storage = transaction.objectStore('SM_GetSettingsBySMCo');
    settings.InsertDate = new Date();
    settings.OrginSMCo = smCo;
    storage.put(settings);
  }

  private saveWCCustomFieldDefs(db: IDBDatabase, wcCustomFieldDefs: any): void {
    const transaction = db.transaction('SM_WCCustomFieldDefs', 'readwrite');
    const storage = transaction.objectStore('SM_WCCustomFieldDefs');
    storage.put(wcCustomFieldDefs);
  }

  private saveCommonLookups(db: IDBDatabase, coCommonLookups: any[], smCo: any): void {
    if (!coCommonLookups) return;
    const transaction = db.transaction('SM_GetCommonLookups', 'readwrite');
    const storage = transaction.objectStore('SM_GetCommonLookups');
    coCommonLookups.forEach(lookup => {
      lookup.InsertDate = new Date();
      lookup.OrginSMCo = smCo;
      storage.put(lookup);
    });
  }

  private saveMaterial(db: IDBDatabase, material: any[]): void {
    if (!material) return;
    const transaction = db.transaction('SM_GetMaterial', 'readwrite');
    const storage = transaction.objectStore('SM_GetMaterial');
    material.forEach(mat => {
      mat.InsertDate = new Date();
      storage.put(mat);
    });
  }

  private saveMaterialWLoc(db: IDBDatabase, materialWLoc: any[]): void {
    if (!materialWLoc) return;
    const transaction = db.transaction('SM_GetMaterialWLoc', 'readwrite');
    const storage = transaction.objectStore('SM_GetMaterialWLoc');
    materialWLoc.forEach(mat => {
      mat.InsertDate = new Date();
      storage.put(mat);
    });
  }

  private saveCommonWoLookups(db: IDBDatabase, commonWoLookups: any[], workOrderID: string): void {
    if (!commonWoLookups) return;
    const transaction = db.transaction('SM_GetCommonWoLookups', 'readwrite');
    const storage = transaction.objectStore('SM_GetCommonWoLookups');
    commonWoLookups.forEach(lookup => {
      lookup.InsertDate = new Date();
      lookup.OrginSMWorkOrderID = workOrderID;
      storage.put(lookup);
    });
  }

  private saveWorkOrderScopesNotHistory(db: IDBDatabase, workOrderScopes: any[], workOrderID: string): void {
    if (!workOrderScopes) return;
    const transaction = db.transaction('SM_GetWorkOrderScopesNotHistory', 'readwrite');
    const storage = transaction.objectStore('SM_GetWorkOrderScopesNotHistory');
    workOrderScopes.forEach(scope => {
      scope.InsertDate = new Date();
      scope.OrginSMWorkOrderID = workOrderID;
      storage.put(scope);
    });
  }

  private saveSignerContacts(db: IDBDatabase, signerContacts: any[], workOrderID: string, tripID: string): void {
    if (!signerContacts) return;
    const transaction = db.transaction('SM_GetSignerContacts', 'readwrite');
    const storage = transaction.objectStore('SM_GetSignerContacts');
    signerContacts.forEach(contact => {
      contact.InsertDate = new Date();
      contact.OrginSMWorkOrderID = workOrderID;
      contact.OrginSMTripID = tripID;
      storage.put(contact);
    });
  }

  private saveServiceItemWOHistory(
    db: IDBDatabase,
    serviceItemWOHistory: any[],
    workOrderID: string,
    serviceSiteID: string,
    tripID: string
  ): void {
    if (!serviceItemWOHistory) return;
    const transaction = db.transaction('SM_GetServiceItemWOHistory', 'readwrite');
    const storage = transaction.objectStore('SM_GetServiceItemWOHistory');
    serviceItemWOHistory.forEach(history => {
      history.InsertDate = new Date();
      history.OrginSMWorkOrderID = workOrderID;
      history.OrginSMServiceSiteID = serviceSiteID;
      history.OrginSMTripID = tripID;
      storage.put(history);
    });
  }

  private saveServiceItemDetails(db: IDBDatabase, serviceItemDetails: any[], workOrderID: string, serviceSiteID: string): void {
    if (!serviceItemDetails) return;
    const transaction = db.transaction('SM_GetServiceItemDetails', 'readwrite');
    const storage = transaction.objectStore('SM_GetServiceItemDetails');
    serviceItemDetails.forEach(item => {
      item.InsertDate = new Date();
      item.OrginSMWorkOrderID = workOrderID;
      item.OrginSMServiceSiteID = serviceSiteID;
      storage.put(item);
    });
  }

  private saveFormTemplateOptions(db: IDBDatabase, formTemplateOptions: any[]): void {
    if (!formTemplateOptions) return;
    const transaction = db.transaction('SM_GetFormTemplateOptions', 'readwrite');
    const storage = transaction.objectStore('SM_GetFormTemplateOptions');
    formTemplateOptions.forEach(option => {
      option.InsertDate = new Date();
      storage.put(option);
    });
  }

  private saveEquipment(db: IDBDatabase, equipment: any[], smCo: string, tripID: string, workOrderScopeID: string): void {
    if (!equipment) return;
    const transaction = db.transaction('SM_GetEquipment', 'readwrite');
    const storage = transaction.objectStore('SM_GetEquipment');
    equipment.forEach(equip => {
      equip.InsertDate = new Date();
      equip.OrginSMCo = smCo;
      equip.OrginSMTripID = tripID;
      equip.OrginSMWorkOrderScopeID = workOrderScopeID;
      storage.put(equip);
    });
  }

  private saveStandardItems(db: IDBDatabase, standardItems: any[], smCo: string, tripID: string, workOrderScopeID: string): void {
    if (!standardItems) return;
    const transaction = db.transaction('SM_GetStandardItems', 'readwrite');
    const storage = transaction.objectStore('SM_GetStandardItems');
    standardItems.forEach(item => {
      item.InsertDate = new Date();
      item.OrginSMCo = smCo;
      item.OrginSMTripID = tripID;
      item.OrginSMWorkOrderScopeID = workOrderScopeID;
      storage.put(item);
    });
  }

  private saveScopeMaterial(db: IDBDatabase, materials: any[], smCo: string, tripID: string, workOrderScopeID: string): void {
    if (!materials) return;
    const transaction = db.transaction('SM_GetMaterial', 'readwrite');
    const storage = transaction.objectStore('SM_GetMaterial');
    materials.forEach(mat => {
      mat.InsertDate = new Date();
      mat.OrginSMCo = smCo;
      mat.OrginSMTripID = tripID;
      mat.OrginSMWorkOrderScopeID = workOrderScopeID;
      storage.put(mat);
    });
  }

  private saveScopeMaterialWLoc(db: IDBDatabase, materialsWLoc: any[], smCo: string, tripID: string, workOrderScopeID: string): void {
    if (!materialsWLoc) return;
    const transaction = db.transaction('SM_GetMaterialWLoc', 'readwrite');
    const storage = transaction.objectStore('SM_GetMaterialWLoc');
    materialsWLoc.forEach(mat => {
      mat.InsertDate = new Date();
      mat.OrginSMCo = smCo;
      mat.OrginSMTripID = tripID;
      mat.OrginSMWorkOrderScopeID = workOrderScopeID;
      storage.put(mat);
    });
  }

  private saveScopeTasks(db: IDBDatabase, scopeTasks: any[], workOrderScopeID: string): void {
    if (!scopeTasks) return;
    const transaction = db.transaction('SM_GetScopeTasks', 'readwrite');
    const storage = transaction.objectStore('SM_GetScopeTasks');
    scopeTasks.forEach(task => {
      task.InsertDate = new Date();
      task.OrginSMWorkOrderScopeID = workOrderScopeID;
      storage.put(task);
    });
  }

  private saveWorkCompleted(db: IDBDatabase, workCompleted: any[], workOrderScopeID: string): void {
    if (!workCompleted) return;
    const transaction = db.transaction('SM_GetWorkCompleted', 'readwrite');
    const storage = transaction.objectStore('SM_GetWorkCompleted');
    workCompleted.forEach(wc => {
      wc.InsertDate = new Date();
      wc.OrginSMWorkOrderScopeID = workOrderScopeID;
      storage.put(wc);
    });
  }

  private saveFormsPerScope(db: IDBDatabase, formsPerScope: any[], workOrderScopeID: string): void {
    if (!formsPerScope) return;
    const transaction = db.transaction('SM_GetFormsPerScope', 'readwrite');
    const storage = transaction.objectStore('SM_GetFormsPerScope');
    formsPerScope.forEach(form => {
      form.InsertDate = new Date();
      form.OrginSMWorkOrderScopeID = workOrderScopeID;
      storage.put(form);
    });
  }

  private saveScopePurchaseOrders(db: IDBDatabase, scopePurchaseOrders: any[], workOrderScopeID: string): void {
    if (!scopePurchaseOrders) return;
    const transaction = db.transaction('SM_GetScopePurchaseOrders', 'readwrite');
    const storage = transaction.objectStore('SM_GetScopePurchaseOrders');
    scopePurchaseOrders.forEach(po => {
      po.InsertDate = new Date();
      po.OrginSMWorkOrderScopeID = workOrderScopeID;
      storage.put(po);
    });
  }
}
